import { AccommodationActivity, ItineraryActivity } from "../models/activity.model";
import { BaseActivityCard } from "./BaseActivityCard";
import { DocumentationList } from "@/components/ui/documentation-list";
import { useI18n } from "@/hooks/useI18n";
import {
  ActivityAddress,
  ActivityDateRange,
  ActivityTime,
  ActivityCost,
  ActivityLink,
  ActivityRoomsGuests,
  ActivityHotelPolicies,
  ActivityNonIncludedTaxes
} from "./ActivityFields";

interface AccommodationCardProps {
  activity: AccommodationActivity;
  onEdit: (activity: ItineraryActivity) => void;
  onDelete: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onSaveToLibrary?: (activity: ItineraryActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  onDragStart?: (activity: ItineraryActivity) => void;
  onDropBefore?: (sourceActivityId: string, targetActivityId?: string) => void;
  onDragEnd?: () => void;
  isTraveler?: boolean;
  isAdmin?: boolean;
  unsaved?: boolean;
}

export const AccommodationCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onSaveToLibrary,
  onExpand,
  isReadOnly = false,
  onDragStart,
  onDropBefore,
  onDragEnd,
  isTraveler = false,
  isAdmin = false,
  unsaved = false,
}: AccommodationCardProps) => {
  const { t } = useI18n();
  const isInActivity = activity.inOrOutActivity === 'in';
  const isOutActivity = activity.inOrOutActivity === 'out';

  const renderAlwaysVisible = () => (
    <>
      <ActivityAddress address={activity.address} />
      <ActivityDateRange start_date={activity.start_date} end_date={activity.end_date} />
      {isInActivity && <ActivityTime time={activity.check_in_time} />}
      {isOutActivity && <ActivityTime time={activity.check_out_time} />}
      {isInActivity && <ActivityCost cost={activity.cost} isAdmin={isAdmin} isTraveler={isTraveler} />}
      {isInActivity && <ActivityRoomsGuests metadata={activity.metadata} />}
    </>
  );

  const renderCollapsible = () => (
    <div className="space-y-2">

      {/* RateHawk: rooms & guests */}
      {activity.metadata?.ratehawk_guests_per_room && activity.metadata.ratehawk_guests_per_room.length > 0 && (
        <div>
          <span className="font-medium mb-1 block">{t('activityCard.roomsAndGuests')}</span>
          <div className="text-gray-600 text-sm space-y-0.5">
            {activity.metadata.ratehawk_guests_per_room.map((room, i) => (
              <p key={i}>
                {t('activityCard.roomLabel', { number: i + 1 })}: {t('activityCard.adultsCount', { count: room.adults })}
                {room.childrenAges.length > 0 &&
                  `, ${t('activityCard.childrenCount', { count: room.childrenAges.length, ages: room.childrenAges.join(", ") })}`}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* RateHawk: hotel policies */}
      <ActivityHotelPolicies metadata={activity.metadata} />

      {/* RateHawk: non-included taxes */}
      <ActivityNonIncludedTaxes metadata={activity.metadata} />

      {/* Check-in Note - Full width */}
      {activity.check_in_note && (
        <div>
          <span className="font-medium mb-1 block">Check-in Note</span>
          <p className="text-gray-600">{activity.check_in_note}</p>
        </div>
      )}

      {/* Documentation */}
      <DocumentationList documentation={activity.documentation || []} />

      {/* Link */}
      <ActivityLink link={activity.link} isAdmin={isAdmin} isTraveler={isTraveler} />
    </div>
  );

  return (
    <BaseActivityCard
      activity={activity}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onSaveToLibrary={onSaveToLibrary}
      onDragStart={onDragStart}
      onDropBefore={onDropBefore}
      onDragEnd={onDragEnd}
      onExpand={onExpand}
      isReadOnly={isReadOnly}
      isTraveler={isTraveler}
      isAdmin={isAdmin}
      unsaved={unsaved}
      media={activity.media}
    >
      {{
        alwaysVisible: renderAlwaysVisible(),
        collapsible: renderCollapsible()
      }}
    </BaseActivityCard>
  );
};
